import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Icon,
  Snackbar,
  TextField,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios.js";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";

function BusinessPage() {
  const pageTitle = "WhatsApp Business";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [business, setBusiness] = useState({});
  const [numbers, setNumbers] = useState([]);
  const [configId, setConfigId] = useState(null);

  const [balance, setBalance] = useState(0);
  const [fundDialogOpen, setFundDialogOpen] = useState(false);
  const [fundAmount, setFundAmount] = useState("");
  const [currency, setCurrency] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(`portal/wacc/businesses/${ref}/fetch.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setCurrency(result.data.currency);
            setBusiness(result.data.data.business || {});
            setNumbers(result.data.data.numbers || []);
            setBalance(result.data.data.balance || 0);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [ref, refreshPage]);

  useEffect(() => {
    if (!window.FB) {
      window.fbAsyncInit = function () {
        window.FB.init({
          appId: window.META_APP_ID,
          cookie: true,
          xfbml: true,
          version: "v25.0",
        });
      };

      const script = document.createElement("script");
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      script.src = "https://connect.facebook.net/en_US/sdk.js";

      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    axios
      .get("portal/wacc/businesses.json")
      .then((result) => {
        if (result.data.status === "success") {
          setConfigId(result.data.data.config_id || null);
        }
      })
      .catch(() => {});
  }, []);

  const addBusinessNumber = () => {
    if (!configId) {
      setSeverity("error");
      setErrMsg("WhatsApp Embedded Signup configuration is unavailable.");
      setOpen(true);
      return;
    }

    if (!window.FB) {
      setSeverity("error");
      setErrMsg("Unable to load Meta Embedded Signup.");
      setOpen(true);
      return;
    }

    setShowLoading(true);

    window.FB.login(
      (response) => {
        if (!response || !response.authResponse || !response.authResponse.code) {
          setShowLoading(false);
          return;
        }

        const code = response.authResponse.code;
        const wabaId = response.authResponse.waba_id || response.authResponse.wabaId || null;
        const phoneNumberId =
          response.authResponse.phone_number_id || response.authResponse.phoneNumberId || null;

        if (!wabaId || !phoneNumberId) {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("Meta did not return the WhatsApp Business Account or phone number.");
          setOpen(true);
          return;
        }

        axios
          .post("portal/wacc/businesses/connect.json", {
            code,
            waba_id: wabaId,
            phone_number_id: phoneNumberId,
          })
          .then((result) => {
            setShowLoading(false);

            if (result.data.status === "success") {
              if (result.data.data.business.reference !== ref) {
                navigate(`/wacc/businesses/${result.data.data.business.reference}`);
                return;
              }

              setBusiness(result.data.data.business);

              setNumbers((current) => [
                ...current.filter((item) => item.reference !== result.data.data.number.reference),
                result.data.data.number,
              ]);
            } else {
              setSeverity("error");
              setErrMsg(result.data.data.message);
              setOpen(true);
            }
          })
          .catch(() => {
            setShowLoading(false);
            setSeverity("error");
            setErrMsg("Unable to connect WhatsApp Business number.");
            setOpen(true);
          });
      },
      {
        config_id: configId,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          sessionInfoVersion: 3,
        },
      }
    );
  };

  const openFundDialog = () => {
    setFundAmount("");
    setFundDialogOpen(true);
  };

  const closeFundDialog = () => {
    if (!showLoading) {
      setFundDialogOpen(false);
      setFundAmount("");
    }
  };

  const fundBusiness = async () => {
    const amount = parseFloat(fundAmount);

    if (!amount || amount <= 0) {
      setSeverity("error");
      setErrMsg("Enter a valid amount.");
      setOpen(true);
      return;
    }

    const amountInKobo = Math.round(amount * 100);

    setFundDialogOpen(false);
    setShowLoading(true);

    await axios
      .post(`portal/wacc/businesses/balance/fund.json`, {
        reference: ref,
        amount: amountInKobo,
      })
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setBalance(result.data.data.balance || 0);

          setFundDialogOpen(false);
          setFundAmount("");

          setSeverity("success");
          setErrMsg("Message balance funded successfully.");
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={fundDialogOpen} fullWidth maxWidth="xs">
        <DialogTitle>Add Funds</DialogTitle>

        <DialogContent>
          <MDBox pt={1}>
            <TextField
              fullWidth
              label="Amount"
              placeholder="0.00"
              type="number"
              value={fundAmount}
              onChange={(event) => setFundAmount(event.target.value)}
              InputProps={{
                startAdornment: (
                  <MDTypography variant="body2" mr={1}>
                    {currency}
                  </MDTypography>
                ),
              }}
              inputProps={{
                min: 1,
                step: 0.01,
              }}
              autoFocus
            />

            <MDTypography variant="caption" color="text" display="block" mt={1}>
              Enter an amount to fund your messaging balance.
            </MDTypography>
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            variant="text"
            color="dark"
            size="small"
            onClick={closeFundDialog}
            disabled={showLoading}
          >
            Cancel
          </MDButton>

          <MDButton
            variant="contained"
            color="dark"
            size="small"
            onClick={fundBusiness}
            disabled={!fundAmount || Number(fundAmount) <= 0 || showLoading}
          >
            Add Funds
          </MDButton>
        </DialogActions>
      </Dialog>

      {pageLoaded && (
        <MDBox mb={5}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3} px={1}>
            <MDBox display="flex" alignItems="center">
              <MDBox>
                <MDTypography variant="h4" fontWeight="medium">
                  {business.title}
                </MDTypography>

                <MDTypography variant="body2" color="text" fontSize={12} mt={0}>
                  WhatsApp Business Account
                </MDTypography>
              </MDBox>
            </MDBox>

            <Card>
              <MDBox display="flex" alignItems="center" gap={3} px={2} py={1}>
                <MDBox>
                  <MDTypography variant="caption" color="text" fontSize={13}>
                    Messaging Balance
                  </MDTypography>

                  <MDTypography variant="body2" fontSize={15} fontWeight="medium">
                    {`${currency}${(Number(balance || 0) / 100).toLocaleString("en-NG", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}
                  </MDTypography>
                </MDBox>

                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  startIcon={<Icon>add</Icon>}
                  onClick={openFundDialog}
                >
                  Add Funds
                </MDButton>
              </MDBox>
            </Card>
          </MDBox>

          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Card>
                <MDBox p={2.5}>
                  <MDTypography variant="h6" fontSize={15} fontWeight="medium">
                    Business Account
                  </MDTypography>

                  <MDBox mt={1}>
                    <MDBox mb={2}>
                      <MDTypography variant="caption" color="text" fontSize={11}>
                        WABA ID
                      </MDTypography>

                      <MDTypography variant="body2" fontSize={14}>
                        {business.wa_waba_id}
                      </MDTypography>
                    </MDBox>

                    <MDBox mb={2}>
                      <MDTypography variant="caption" color="text" fontSize={11}>
                        Account Review
                      </MDTypography>

                      <MDTypography variant="body2" fontSize={14}>
                        {business.wa_account_review_status || "---"}
                      </MDTypography>
                    </MDBox>

                    <MDBox mb={2}>
                      <MDTypography variant="caption" color="text" fontSize={11}>
                        Business Verification
                      </MDTypography>

                      <MDTypography variant="body2" fontSize={14}>
                        {business.wa_business_verification_status || "---"}
                      </MDTypography>
                    </MDBox>

                    <MDBox>
                      <MDTypography variant="caption" color="text" fontSize={11}>
                        Webhook
                      </MDTypography>

                      <MDTypography
                        variant="body2"
                        fontSize={14}
                        fontWeight="medium"
                        color={business.wa_webhook_subscribed ? "success" : "error"}
                      >
                        {business.wa_webhook_subscribed ? "Connected" : "Not connected"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={7}>
              <Card>
                <MDBox p={2.5}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="top" mb={2}>
                    <MDTypography variant="h6" fontSize={15} fontWeight="medium">
                      Whatsapp Numbers
                      <br />
                      <MDTypography variant="body2" fontSize={13} color="text">
                        {numbers.length} {numbers.length === 1 ? "number" : "numbers"}
                      </MDTypography>
                    </MDTypography>

                    <MDBox>
                      <MDButton
                        variant="contained"
                        color="dark"
                        size="small"
                        startIcon={<Icon>add</Icon>}
                        onClick={addBusinessNumber}
                      >
                        Add Number
                      </MDButton>
                    </MDBox>
                  </MDBox>

                  {numbers.length === 0 ? (
                    <MDBox py={5} textAlign="center">
                      <Icon
                        sx={{
                          fontSize: 45,
                          opacity: 0.35,
                          mb: 1,
                        }}
                      >
                        phone
                      </Icon>

                      <MDTypography variant="body2" fontSize={14}>
                        No business numbers connected.
                      </MDTypography>
                    </MDBox>
                  ) : (
                    numbers.map((number) => (
                      <MDBox
                        key={number.reference}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        py={2}
                        onClick={() => navigate(`/wacc/businesses/number/${number.reference}`)}
                        sx={{
                          borderBottom: "1px solid #eeeeee",
                          cursor: "pointer",
                          "&:last-child": {
                            borderBottom: "none",
                          },
                          "&:hover": {
                            backgroundColor: "#fafafa",
                          },
                        }}
                      >
                        <MDBox display="flex" alignItems="center">
                          <MDBox
                            width="40px"
                            height="40px"
                            borderRadius="50%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                              backgroundColor: "#e8f5e9",
                            }}
                          >
                            <Icon color="success">phone</Icon>
                          </MDBox>

                          <MDBox ml={1.5}>
                            <MDTypography variant="body2" fontSize={14} fontWeight="medium">
                              {number.title}
                            </MDTypography>

                            <MDTypography variant="body2" fontSize={13} color="text">
                              {number.phone_number}
                            </MDTypography>
                          </MDBox>
                        </MDBox>

                        <MDBox textAlign="right">
                          <MDTypography variant="caption" fontSize={10} color="text">
                            Phone Number ID
                          </MDTypography>

                          <MDTypography variant="body2" fontSize={11} color="text">
                            {number.wa_reference}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    ))
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>

          <MDBox mt={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <MDBox p={2.5} display="flex" alignItems="center" justifyContent="space-between">
                    <MDBox>
                      <MDTypography variant="h6" fontSize={15} fontWeight="medium">
                        Message Templates
                      </MDTypography>

                      <MDTypography variant="body2" fontSize={13} color="text" mt={0.5}>
                        Manage WhatsApp message templates for this business.
                      </MDTypography>
                    </MDBox>

                    <MDButton
                      variant="outlined"
                      color="dark"
                      size="small"
                      startIcon={<Icon>message</Icon>}
                      onClick={() => navigate(`/wacc/businesses/${ref}/templates`)}
                    >
                      Templates
                    </MDButton>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <MDBox p={2.5} display="flex" alignItems="center" justifyContent="space-between">
                    <MDBox>
                      <MDTypography variant="h6" fontSize={15} fontWeight="medium">
                        Message Broadcasts
                      </MDTypography>

                      <MDTypography variant="body2" fontSize={13} color="text" mt={0.5}>
                        Broadcast marketing messages to your customers for this business.
                      </MDTypography>
                    </MDBox>

                    <MDButton
                      variant="outlined"
                      color="dark"
                      size="small"
                      startIcon={<Icon>campaign</Icon>}
                      onClick={() => navigate(`/wacc/broadcasts/${ref}`)}
                    >
                      Broadcasts
                    </MDButton>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default BusinessPage;
